import type { CSSProperties } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrency } from '../../utils/formatters';

type RevenueGraphProps = {
  data: Array<{
    request_manager: string;
    status: string;
    estimated_revenue: number;
    arrival_year: number;
    arrival_month: number;
    request_year: number;
    request_month: number;
  }>;
  useRequestMonth?: boolean;
  selectedYears?: number[];
  chartType?: 'line' | 'bar';
};

/** Y-axis compact labels (millions €) — aligned with dashboard typography */
function formatToMillionEuros(value: number): string {
  const millions = (value / 1000000).toFixed(1);
  const formatted = millions.endsWith('.0') ? millions.slice(0, -2) : millions;
  return `${formatted}M€`;
}

/** Distinct categorical hues: brand teal first, then well-separated colors for multi-year lines/bars. */
const YEAR_SERIES_COLORS = [
  '#188d82', // action teal (primary)
  '#2563eb', // blue
  '#d97706', // amber
  '#7c3aed', // violet
  '#db2777', // pink
  '#ea580c', // orange
  '#0891b2', // cyan
  '#4f46e5', // indigo
];

const axisTick = { fill: '#64748b', fontSize: 11 };
const axisLine = { stroke: '#e2e8f0' };
/** Slightly stronger than axis ticks so month columns read clearly behind series */
const gridStroke = '#cbd5e1';

const tooltipContentStyle: CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid rgb(226 232 240)',
  borderRadius: '0.5rem',
  boxShadow: '0 4px 6px -1px rgb(15 23 42 / 0.08), 0 2px 4px -2px rgb(15 23 42 / 0.06)',
  fontSize: '12px',
  padding: '10px 12px',
};

const tooltipLabelStyle: CSSProperties = {
  color: '#475569',
  fontWeight: 600,
  marginBottom: '4px',
};

const legendWrapperStyle: CSSProperties = {
  paddingTop: '12px',
  fontSize: '12px',
};

export function RevenueGraph({ data, useRequestMonth = false, selectedYears, chartType = 'line' }: RevenueGraphProps) {
  const revenueByYearMonth = data
    .filter(row => row.status && row.status.toLowerCase() === 'booked')
    .reduce((acc, row) => {
      const year = useRequestMonth ? row.request_year : row.arrival_year;
      const month = useRequestMonth ? row.request_month : row.arrival_month;

      if (selectedYears && !selectedYears.includes(year)) {
        return acc;
      }

      if (!acc[year]) {
        acc[year] = Array(12).fill(0);
      }
      acc[year][month - 1] += row.estimated_revenue || 0;
      return acc;
    }, {} as Record<number, number[]>);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const chartData = months.map((month, index) => {
    const dataPoint: Record<string, string | number> = { month };
    Object.keys(revenueByYearMonth).forEach(year => {
      dataPoint[`${year}`] = revenueByYearMonth[Number(year)][index];
    });
    return dataPoint;
  });

  const years = Object.keys(revenueByYearMonth).sort((a, b) => Number(a) - Number(b));
  const title = useRequestMonth ? 'Confirmed revenue by request month' : 'Confirmed revenue by arrival month';

  const chartInner = (
    <ResponsiveContainer width="100%" height="100%">
      {chartType === 'bar' ? (
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 4 }} barCategoryGap="12%">
          <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal vertical />
          <XAxis
            dataKey="month"
            tick={axisTick}
            axisLine={axisLine}
            tickLine={{ stroke: '#e2e8f0' }}
            dy={4}
          />
          <YAxis
            tickFormatter={formatToMillionEuros}
            width={56}
            tick={axisTick}
            axisLine={axisLine}
            tickLine={{ stroke: '#e2e8f0' }}
          />
          <Tooltip
            formatter={(value: number, name: string) => [formatCurrency(value), String(name)]}
            labelFormatter={String}
            labelStyle={tooltipLabelStyle}
            contentStyle={tooltipContentStyle}
            cursor={{ fill: 'rgb(24 141 130 / 0.06)' }}
          />
          <Legend wrapperStyle={legendWrapperStyle} iconType="square" iconSize={10} />
          {years.map((year, index) => (
            <Bar
              key={year}
              dataKey={year}
              fill={YEAR_SERIES_COLORS[index % YEAR_SERIES_COLORS.length]}
              name={year}
              radius={[4, 4, 0, 0]}
              maxBarSize={44}
            />
          ))}
        </BarChart>
      ) : (
        <LineChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal vertical />
          <XAxis
            dataKey="month"
            tick={axisTick}
            axisLine={axisLine}
            tickLine={{ stroke: '#e2e8f0' }}
            dy={4}
          />
          <YAxis
            tickFormatter={formatToMillionEuros}
            width={56}
            tick={axisTick}
            axisLine={axisLine}
            tickLine={{ stroke: '#e2e8f0' }}
          />
          <Tooltip
            formatter={(value: number, name: string) => [formatCurrency(value), String(name)]}
            labelFormatter={String}
            labelStyle={tooltipLabelStyle}
            contentStyle={tooltipContentStyle}
            cursor={{ stroke: 'rgb(24 141 130 / 0.35)', strokeWidth: 1 }}
          />
          <Legend wrapperStyle={legendWrapperStyle} iconType="line" iconSize={12} />
          {years.map((year, index) => {
            const c = YEAR_SERIES_COLORS[index % YEAR_SERIES_COLORS.length];
            return (
              <Line
                key={year}
                type="monotone"
                dataKey={year}
                stroke={c}
                strokeWidth={2.25}
                name={year}
                dot={{ r: 3.5, fill: '#fff', stroke: c, strokeWidth: 2 }}
                activeDot={{ r: 5, fill: c, stroke: '#fff', strokeWidth: 2 }}
              />
            );
          })}
        </LineChart>
      )}
    </ResponsiveContainer>
  );

  return (
    <section
      className="mb-6 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm"
      aria-label={title}
    >
      <div className="border-b border-slate-200/80 bg-slate-50/80 px-5 py-3.5">
        <h3 className="text-sm font-semibold tracking-tight text-slate-800">{title}</h3>
      </div>
      <div className="px-3 pb-5 pt-4 sm:px-5">
        {years.length === 0 ? (
          <div className="flex h-[280px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/50 text-center">
            <p className="text-sm font-medium text-slate-600">No data for this chart</p>
            <p className="max-w-sm text-xs text-slate-500">
              Adjust filters or years so booked groups fall in the selected period.
            </p>
          </div>
        ) : (
          <div className="h-[300px] sm:h-[320px]">{chartInner}</div>
        )}
      </div>
    </section>
  );
}
